import { reactive, type UnwrapNestedRefs } from 'vue';
import type { VideoJsPlayer } from 'video.js';
import { getNextMovieGeoIndex } from 'src/lib/moviePlayerHelper';
import { logMoviePlaySeconds } from 'src/lib/analyticsHelper';
import { PLAY_MSEC_SEND_THRES } from 'src/consts/movie_player';
import type {
  AnalyticsParams,
  BothLeftRight,
  LeftRight,
  MoviePlayerCompareModeState,
} from 'src/models/moviePlayer';

const PLAYER_TYPE = 'compare';

interface Deps {
  updateTitleBarTexts: (lr: LeftRight) => void;
  pauseUI: (lr_: BothLeftRight) => void;
  playNextMovie: (leftRight: LeftRight) => Promise<void>;
  playPreviousMovie: (leftRight: LeftRight) => Promise<void>;
  analytics: UnwrapNestedRefs<AnalyticsParams>;
}

interface MoviePlayerCompareModeTimeUpdateContext {
  onVjsTimeUpdate: (lr: LeftRight, vjs: VideoJsPlayer) => void;
}

// useMoviePlayerCompareModePlayback.tsから分割したvideo.jsのtimeupdateイベントハンドラ。
// シークバー・mgi・動画終端判定・再生時間analytics送信をここに集約する。
// 動画切替（movieTransition）・再生停止（control）はdeps経由でのみ呼び出し、
// useMoviePlayerCompareModeVjs.tsは本ファイルをimportしない（コールバック注入で結線する）。
export function useMoviePlayerCompareModeTimeUpdate(
  state: UnwrapNestedRefs<MoviePlayerCompareModeState>,
  deps: Deps,
): UnwrapNestedRefs<MoviePlayerCompareModeTimeUpdateContext> {
  const trySetNextMovieGeoIndex = (lr: LeftRight) => {
    const currentMovie = state[lr].movieList.current.currentMovie;
    if (!currentMovie) return;

    const next = getNextMovieGeoIndex(currentMovie.id, currentMovie.movieGeoIndices, state[lr].geo.currentMgiIdx);
    if (next) {
      state[lr].geo.currentMgi = next.mgi;
      state[lr].geo.currentMgiIdx = next.idx;
    }
    deps.updateTitleBarTexts(lr);
  };
  const onVjsTimeUpdate = (lr: LeftRight, vjs: VideoJsPlayer) => {
    if (state[lr].playback.isSeeking || state[lr].movieList.current.isTryingToPlayAnotherMovie) return;

    const currentMovie = state[lr].movieList.current.currentMovie;
    if (!currentMovie) return;

    // update seekbar
    const currentTimeMsec = parseInt((vjs.currentTime() * 1000).toString());
    const playOffsetDiff =
      currentTimeMsec - (currentMovie.playStartOffsetMsec || 0);
    state[lr].playback.seek.current = (currentMovie.accumMsec || 0) + playOffsetDiff;
    // 現在の再生位置が実際に撮影された時刻
    if (currentMovie.ts) {
      state[lr].geo.currentMovieCurrentDt =
        new Date(currentMovie.ts.valueOf() + currentTimeMsec);
    }
    // mgiの更新
    const currentMgi = state[lr].geo.currentMgi;
    if (currentMgi) {
      if (currentMgi.endMsecDiff && currentMgi.endMsecDiff <= currentTimeMsec) {
        trySetNextMovieGeoIndex(lr);
      }
    }
    // サーバ側で保持しているdurationはmtxをベースに記録しているが、
    // 動画ファイルそのもののdurationは
    // 若干誤差があったりするのでケア.
    if (!state[lr].playback.isPlayBackward) {
      const endMsec = Math.min(vjs.duration() * 1000, currentMovie.hardEndOffsetMsec || 0);
      if (endMsec <= currentTimeMsec) {
        if (state[lr].movieList.current.isLastMovie) {
          // 終わり
          deps.pauseUI(lr);
        } else {
          // 次の動画を再生
          deps.playNextMovie(lr);
        }
      }
    } else {
      const startMsec = Math.min(vjs.duration() * 1000, (currentMovie.hardStartOffsetMsec || 0));
      if (startMsec >= currentTimeMsec) {
        if (state[lr].movieList.current.isFirstMovie) {
          // 終わり
          deps.pauseUI(lr);
        } else {
          // 次の動画を再生
          deps.playPreviousMovie(lr);
        }
      }
    }
    if (lr === 'left') {
      if (
        deps.analytics.tmpCalcBase === -1 ||
        currentTimeMsec < deps.analytics.tmpCalcBase
      ) {
        deps.analytics.tmpCalcBase = currentTimeMsec;
      }
      const playedMsec = currentTimeMsec - deps.analytics.tmpCalcBase;
      deps.analytics.accumulatedPlayMsecs += playedMsec;
      if (deps.analytics.accumulatedPlayMsecs >= PLAY_MSEC_SEND_THRES) {
        const seconds = parseInt((deps.analytics.accumulatedPlayMsecs / 1000).toString());
        logMoviePlaySeconds(PLAYER_TYPE, seconds);
        deps.analytics.accumulatedPlayMsecs = 0;
      }
      deps.analytics.tmpCalcBase = currentTimeMsec;
    }
  };

  return reactive({ onVjsTimeUpdate });
}
